import { IDBPDatabase, openDB } from 'idb';
import { debounce } from 'lodash';
import { ISloydStore } from '../types';

const DATABASE_NAME = 'SloydDB';
const DATABASE_VERSION = 1;
const STORE_NAME = 'SloydStore';

let dbPromise: Promise<IDBPDatabase> | any | null = null;

export const initIdb = () => {
  dbPromise = openDB(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    },
  }).catch((err) => {
    console.error(`Failed to open IndexedDB '${DATABASE_NAME}':`, err);
    dbPromise = null;
  });
};

const getDb = async (): Promise<IDBPDatabase | null> => {
  if (!dbPromise) {
    console.warn(`IndexedDB '${DATABASE_NAME}' is not initialized.`);
    return null;
  }
  try {
    return await dbPromise;
  } catch (err) {
    console.error(`Error accessing '${DATABASE_NAME}':`, err);
    return null;
  }
};

export const idbSaveState = async (state: Partial<ISloydStore>) => {
  try {
    const db = await getDb();
    if (!db) return;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await tx.store.put(state, 'state');
    await tx.done;
  } catch (err) {
    console.error('Error saving state:', err);
  }
};

export const idbPatchState = async (patch: Partial<ISloydStore>) => {
  try {
    const db = await getDb();
    if (!db) return;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const state = await tx.store.get('state');
    await tx.store.put({ ...state, ...patch }, 'state');
    await tx.done;
  } catch (err) {
    console.error('Error patching state:', err);
  }
};

export const idbRestoreState = async (): Promise<Partial<ISloydStore>> => {
  try {
    const db = await getDb();
    if (!db) return {};
    return (await db.get(STORE_NAME, 'state')) || {};
  } catch (err) {
    console.error('Error restoring state:', err);
    return {};
  }
};

export const idbClearState = async () => {
  try {
    const db = await getDb();
    if (!db) return;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await tx.store.clear();
    await tx.done;
  } catch (err) {
    console.error('Error clearing state:', err);
  }
};

export const saveStateDebounced = debounce(idbSaveState, 1000);

// === Your existing code ===
// (keep all as is)

// === New functions for saving/fetching images ===

const IMAGE_PREFIX = 'image:';

/**
 * Save a base64 image into IndexedDB
 * @param key unique identifier for the image
 * @param base64Data base64-encoded image string
 */
export const idbSaveImage = async (imageId: string, base64Data: string) => {
  try {
    const db = await getDb();
    if (!db) return;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await tx.store.put(base64Data, `${IMAGE_PREFIX}${imageId}`);
    await tx.done;
  } catch (err) {
    console.error('Error saving image:', err);
  }
};

/**
 * Load a base64 image from IndexedDB
 * @param key unique identifier for the image
 * @returns base64-encoded image string or null
 */
export const idbLoadImage = async (imageId: string): Promise<string | null> => {
  try {
    const db = await getDb();
    if (!db) return null;
    const fullKey = `${IMAGE_PREFIX}${imageId}`;
    const data = await db.get(STORE_NAME, fullKey);
    return data || null;
  } catch (err) {
    console.error('Error loading image:', err);
    return null;
  }
};
